import { useEffect, useMemo, useState } from "react";
import {
  Sparkles, Palette, MapPin, Calendar, ChevronRight, X, CheckCircle2,
  Sun, Music2, Truck, ShieldCheck, ArrowRight, QrCode,
} from "lucide-react";

/**
 * /demo/moke — Isolated Lifestyle Booking Funnel (MOKE Experience · Cyclades)
 *
 * Standalone showroom page — deliberately DOES NOT touch any real
 * booking / vehicle / admin state. Pure frontend presentation layer for
 * demos. All state lives inside this component; no API calls.
 *
 * v4.0.4 (Feb 28, 2026) — Initial ship.
 */

const MOKE_VARIANTS = [
  { id: 'blue',   name: 'Wave Blue',        hex: '#3b82f6', badgeBg: '#3b82f6', badgeText: '#ffffff', img: '/images/moke/moke-blue.webp' },
  { id: 'yellow', name: 'Sunlight Yellow',  hex: '#facc15', badgeBg: '#fef08a', badgeText: '#854d0e', img: '/images/moke/moke-yellow.webp' },
  { id: 'orange', name: 'Sunset Orange',    hex: '#f97316', badgeBg: '#ffedd5', badgeText: '#9a3412', img: '/images/moke/moke-orange.webp' },
  { id: 'mint',   name: 'Fresh Mint',       hex: '#86efac', badgeBg: '#bbf7d0', badgeText: '#166534', img: '/images/moke/moke-mint.webp' },
  { id: 'grey',   name: 'Standard Black',   hex: '#262626', badgeBg: '#e5e7eb', badgeText: '#111827', img: '/images/moke/moke-grey.webp' },
  { id: 'green',  name: 'Palm Green',       hex: '#15803d', badgeBg: '#bbf7d0', badgeText: '#14532d', img: '/images/moke/moke-green.webp' },
  { id: 'creme',  name: 'Shell White',      hex: '#fef3c7', badgeBg: '#fef3c7', badgeText: '#78350f', border: true, img: '/images/moke/moke-creme.webp' },
];

const EXTRAS = [
  { key: "bimini",    label: "Canvas Bimini Sun Top",     price: 25, type: "per_day", icon: Sun,        blurb: "UV-90 canvas canopy · 60-second deploy" },
  { key: "jbl",       label: "JBL Waterproof Sound",       price: 12, type: "per_day", icon: Music2,     blurb: "Twin marine-grade speakers · pairs to phone" },
  { key: "delivery",  label: "VIP Beach Delivery & Return",price: 40, type: "flat",    icon: Truck,      blurb: "White-glove drop-off at any Cyclades beach" },
  { key: "insurance", label: "Full Premium Island Insurance", price: 20, type: "per_day", icon: ShieldCheck, blurb: "Zero-deductible · covers everything but the sunset" },
];

const BASE_RATE = 120;   // €/day
const VAT_RATE  = 0.24;  // 24% VAT-inclusive display

// —— Helpers ————————————————————————————————————————————————
const fmt = (n) => `€${Number(n || 0).toFixed(2)}`;
const todayISO = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, days) => {
  const d = new Date(iso); d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};
const daysBetween = (a, b) => {
  const ms = new Date(b).getTime() - new Date(a).getTime();
  return Math.max(1, Math.round(ms / 86_400_000));
};

export default function MokeDemo() {
  const [selectedVariant, setSelectedVariant] = useState(MOKE_VARIANTS[0]); // Wave Blue default
  // Double-buffered image swap · fades the current hero to 20% + slight
  // scale-down before painting the next variant, then fades back in.
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [selectedExtras, setSelectedExtras] = useState({}); // { key: true }
  const [pickup, setPickup] = useState(todayISO());
  const [ret,    setRet]    = useState(addDays(todayISO(), 3));
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  // Pre-cache every variant image on mount so subsequent swatch clicks
  // have zero network latency and the fade transition can rely purely
  // on CSS opacity/scale timing.
  useEffect(() => {
    MOKE_VARIANTS.forEach((variant) => {
      const img = new Image();
      img.src = variant.img;
    });
  }, []);

  const selectVariant = (variant) => {
    if (variant.id === selectedVariant.id) return; // no-op if already active
    setIsTransitioning(true);
    setTimeout(() => {
      setSelectedVariant(variant);
      setIsTransitioning(false);
    }, 150);
  };

  const days = useMemo(() => daysBetween(pickup, ret), [pickup, ret]);

  const pricing = useMemo(() => {
    const base = BASE_RATE * days;
    let addons = 0;
    for (const x of EXTRAS) {
      if (!selectedExtras[x.key]) continue;
      addons += x.type === "per_day" ? x.price * days : x.price;
    }
    const subtotal = base + addons;
    // VAT-inclusive → break out the tax portion for the summary line.
    const tax   = subtotal - subtotal / (1 + VAT_RATE);
    return {
      base, addons, subtotal, tax,
      total: subtotal,
    };
  }, [days, selectedExtras]);

  const toggleExtra = (k) =>
    setSelectedExtras((s) => ({ ...s, [k]: !s[k] }));

  return (
    <div
      className="min-h-screen text-neutral-900"
      style={{ background: "#FBF7EF" }}
      data-testid="moke-demo-page"
    >
      {/* ── Lifestyle Header ─────────────────────────────────────── */}
      <header
        className="sticky top-0 z-30 border-b border-black/10 backdrop-blur-xl"
        style={{ background: "rgba(251,247,239,0.85)" }}
        data-testid="moke-header"
      >
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-9 h-9 rounded-full grid place-items-center text-lg"
              style={{ background: "#111", color: "#F5C443" }}
              aria-hidden="true"
            >
              ◐
            </div>
            <div className="min-w-0">
              <p className="font-display text-lg leading-none truncate">
                MOKE Experience <span className="text-neutral-400">· Cyclades Demo</span>
              </p>
              <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mt-1">
                Sun-soaked island rentals
              </p>
            </div>
          </div>
          <span
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] font-semibold px-3 py-1.5 rounded-full border"
            style={{ background: "#111", color: "#F5C443", borderColor: "#111" }}
            data-testid="moke-demo-badge"
          >
            <Sparkles size={11} /> Interactive Demo Mode
          </span>
        </div>
      </header>

      {/* ── Hero + Configurator ──────────────────────────────────── */}
      <section className="max-w-[1240px] mx-auto px-6 lg:px-10 pt-12 lg:pt-16 pb-10">
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-14 items-start">
          {/* Left · Vehicle hero */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.4em] text-neutral-500">
              Model 001 · Mini Moke
            </p>
            <h1 className="font-display text-4xl md:text-5xl lg:text-5xl leading-[1.15] mt-4 max-w-[560px]">
              Chase the horizon in{" "}
              <span
                className="inline-block px-3 pb-1 rounded whitespace-nowrap"
                style={{ background: selectedVariant.badgeBg, color: selectedVariant.badgeText, transition: "background .35s ease, color .35s ease" }}
                data-testid="moke-hero-color-badge"
              >
                {selectedVariant.name.toLowerCase()}
              </span>.
            </h1>
            <p className="text-neutral-600 mt-5 text-base max-w-lg">
              Open-top, canvas-roofed, unapologetically slow. Configure your
              Moke the way you'd order a cocktail — a splash of colour, a
              little sound, and someone to bring it to the beach.
            </p>

            {/* Vehicle stage */}
            <div
              className="relative mt-10 rounded-3xl overflow-hidden shadow-[0_30px_60px_-30px_rgba(0,0,0,0.35)]"
              style={{
                background: `linear-gradient(135deg, ${selectedVariant.hex}22 0%, ${selectedVariant.hex}55 100%)`,
                transition: "background .35s ease",
              }}
              data-testid="moke-hero-stage"
            >
              <div className="w-full flex items-end justify-center min-h-[340px] pt-14 pb-6 pl-2 pr-4">
                <img
                  src={selectedVariant.img}
                  alt={selectedVariant.name}
                  data-testid="moke-hero-image"
                  className={`w-full max-w-[620px] h-auto object-contain mx-auto select-none transition-all duration-300 ease-in-out transform drop-shadow-sm -translate-x-2 ${
                    isTransitioning ? "opacity-20 scale-[0.98]" : "opacity-100 scale-100"
                  }`}
                />
              </div>
              <div className="absolute top-5 left-5 flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-700">
                <span
                  className="w-1.5 h-1.5 rounded-full inline-block animate-pulse"
                  style={{ background: selectedVariant.hex }}
                />
                Live configurator
              </div>
              <div className="absolute top-5 right-5 text-right">
                <p className="text-[10px] uppercase tracking-widest text-neutral-600">Base rate</p>
                <p className="font-display text-2xl leading-none mt-1" data-testid="moke-base-rate">€{BASE_RATE}/day</p>
              </div>
            </div>
          </div>

          {/* Right · Color picker + Extras */}
          <div className="lg:pt-6 space-y-8">
            {/* Colour picker */}
            <div>
              <div className="flex items-baseline justify-between gap-3">
                <h2 className="font-display text-2xl inline-flex items-center gap-2">
                  <Palette size={18} className="text-neutral-500" /> Colourway
                </h2>
                <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                  Pick one · updates instantly
                </span>
              </div>
              <div className="mt-5 flex flex-wrap gap-3" data-testid="moke-color-swatches">
                {MOKE_VARIANTS.map((v) => {
                  const isOn = selectedVariant.id === v.id;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => selectVariant(v)}
                      className={`group flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border transition-all ${
                        isOn
                          ? "border-black shadow-[0_6px_14px_-8px_rgba(0,0,0,0.5)]"
                          : "border-black/10 hover:border-black/40"
                      }`}
                      style={{ background: isOn ? "#111" : "transparent", color: isOn ? "#fff" : "#111" }}
                      data-testid={`moke-color-${v.id}`}
                      aria-pressed={isOn}
                    >
                      <span
                        className={`w-6 h-6 rounded-full ${v.border ? "border border-neutral-300" : "border border-black/10"}`}
                        style={{ background: v.hex }}
                      />
                      <span className="text-xs font-medium tracking-tight">{v.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Date range */}
            <div>
              <div className="flex items-baseline justify-between gap-3">
                <h2 className="font-display text-2xl inline-flex items-center gap-2">
                  <Calendar size={18} className="text-neutral-500" /> Rental dates
                </h2>
                <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-500" data-testid="moke-days-badge">
                  {days} day{days === 1 ? "" : "s"}
                </span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-1.5">Pickup</span>
                  <input
                    type="date"
                    value={pickup}
                    onChange={(e) => {
                      const v = e.target.value; setPickup(v);
                      if (new Date(v) >= new Date(ret)) setRet(addDays(v, 1));
                    }}
                    className="w-full bg-white border border-black/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-black transition-colors"
                    data-testid="moke-pickup-date"
                  />
                </label>
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-1.5">Return</span>
                  <input
                    type="date"
                    value={ret}
                    min={addDays(pickup, 1)}
                    onChange={(e) => setRet(e.target.value)}
                    className="w-full bg-white border border-black/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-black transition-colors"
                    data-testid="moke-return-date"
                  />
                </label>
              </div>
              <p className="mt-3 text-xs text-neutral-500 inline-flex items-center gap-1.5">
                <MapPin size={12} /> Delivered anywhere on the island · Paros · Antiparos
              </p>
            </div>

            {/* Extras */}
            <div>
              <div className="flex items-baseline justify-between gap-3">
                <h2 className="font-display text-2xl">Equipment</h2>
                <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">Toggle · live price</span>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-2.5" data-testid="moke-extras-list">
                {EXTRAS.map((x) => {
                  const isOn = !!selectedExtras[x.key];
                  const Icon = x.icon;
                  return (
                    <button
                      key={x.key}
                      type="button"
                      onClick={() => toggleExtra(x.key)}
                      className={`w-full text-left flex items-center gap-4 px-4 py-3 rounded-xl border transition-all ${
                        isOn
                          ? "border-black bg-white shadow-[0_10px_25px_-18px_rgba(0,0,0,0.6)]"
                          : "border-black/10 bg-white/50 hover:border-black/40"
                      }`}
                      data-testid={`moke-extra-${x.key}`}
                      aria-pressed={isOn}
                    >
                      <div
                        className={`w-10 h-10 rounded-full grid place-items-center transition-colors flex-shrink-0 ${
                          isOn ? "bg-black text-white" : "bg-neutral-100 text-neutral-500"
                        }`}
                      >
                        <Icon size={16} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">{x.label}</p>
                        <p className="text-xs text-neutral-500 mt-0.5 truncate">{x.blurb}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm tabular-nums font-semibold">+€{x.price}</p>
                        <p className="text-[10px] uppercase tracking-widest text-neutral-500">
                          {x.type === "per_day" ? "per day" : "flat"}
                        </p>
                      </div>
                      <span
                        aria-hidden="true"
                        className={`w-5 h-5 rounded-full border grid place-items-center transition-colors ${
                          isOn ? "bg-black border-black text-white" : "border-black/20 text-transparent"
                        }`}
                      >
                        <CheckCircle2 size={14} />
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Sticky Live Total Bar ────────────────────────────────── */}
      <div
        className="sticky bottom-0 z-30 border-t border-black/10 backdrop-blur-xl"
        style={{ background: "rgba(251,247,239,0.9)" }}
        data-testid="moke-sticky-total"
      >
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">Base ({days}d)</p>
              <p className="text-sm tabular-nums" data-testid="moke-total-base">{fmt(pricing.base)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">Options</p>
              <p className="text-sm tabular-nums" data-testid="moke-total-addons">{fmt(pricing.addons)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">VAT (24% incl.)</p>
              <p className="text-sm tabular-nums text-neutral-500" data-testid="moke-total-vat">{fmt(pricing.tax)}</p>
            </div>
            <div className="pl-4 border-l border-black/10">
              <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">Total</p>
              <p className="font-display text-2xl tabular-nums" data-testid="moke-total-grand">{fmt(pricing.total)}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setCheckoutOpen(true)}
            className="group inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-neutral-800 transition-colors"
            data-testid="moke-complete-cta"
          >
            Complete Demo Reservation
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </div>

      {/* Footer disclaimer */}
      <footer className="max-w-[1240px] mx-auto px-6 lg:px-10 py-10 text-center text-xs text-neutral-500">
        This is a presentation demo — no real reservation is created, no data is sent.
      </footer>

      {checkoutOpen && (
        <CheckoutModal
          onClose={() => setCheckoutOpen(false)}
          color={selectedVariant}
          days={days}
          pickup={pickup}
          ret={ret}
          selectedExtras={selectedExtras}
          pricing={pricing}
        />
      )}
    </div>
  );
}

// —— Checkout Modal ————————————————————————————————————————
function CheckoutModal({ onClose, color, days, pickup, ret, selectedExtras, pricing }) {
  const [name,  setName]  = useState("");
  const [phone, setPhone] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const reference = "MOKE-2026-" + Math.random().toString(36).slice(2, 6).toUpperCase();

  const chosen = EXTRAS.filter((x) => selectedExtras[x.key]);

  const canConfirm = name.trim().length >= 2 && phone.trim().length >= 5;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="moke-checkout-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-lg w-full my-8 overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {!confirmed ? (
          <>
            <div className="flex items-start justify-between px-7 pt-6 pb-2">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Demo checkout</p>
                <h3 className="font-display text-2xl mt-1">One last thing.</h3>
              </div>
              <button
                onClick={onClose}
                className="text-neutral-400 hover:text-black"
                aria-label="Close"
                data-testid="moke-modal-close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="px-7 pb-6 pt-3 space-y-5">
              <div className="grid grid-cols-1 gap-3">
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-1.5">Guest name</span>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-white border border-black/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-black transition-colors"
                    placeholder="Ada Lovelace"
                    data-testid="moke-modal-name"
                  />
                </label>
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-1.5">Mobile phone</span>
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full bg-white border border-black/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-black transition-colors"
                    placeholder="+30 691 234 5678"
                    data-testid="moke-modal-phone"
                  />
                </label>
              </div>

              <div className="rounded-xl border border-black/10 p-4 space-y-2 bg-neutral-50">
                <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">Summary</p>
                <SummaryRow label="Vehicle" value={<span>Mini Moke · <span style={{ color: color.hex }} className="font-medium">{color.name}</span></span>} />
                <SummaryRow label="Dates" value={`${pickup} → ${ret} · ${days}d`} />
                <SummaryRow label={`Base (${days}d)`} value={fmt(pricing.base)} />
                {chosen.map((x) => (
                  <SummaryRow key={x.key} label={x.label} value={`+${fmt(x.type === "per_day" ? x.price * days : x.price)}`} muted />
                ))}
                <SummaryRow label="VAT (24% incl.)" value={fmt(pricing.tax)} muted />
                <div className="pt-2 mt-1 border-t border-black/10 flex items-baseline justify-between">
                  <span className="text-xs uppercase tracking-widest text-neutral-500">Total</span>
                  <span className="font-display text-xl tabular-nums" data-testid="moke-modal-total">{fmt(pricing.total)}</span>
                </div>
              </div>

              <button
                type="button"
                disabled={!canConfirm}
                onClick={() => setConfirmed(true)}
                className="w-full inline-flex items-center justify-center gap-2 bg-black text-white py-3 rounded-full text-sm font-medium hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="moke-modal-confirm"
              >
                Confirm &amp; Generate Pass <ChevronRight size={16} />
              </button>
              <p className="text-[10px] text-center text-neutral-400 italic">
                No card required · Demo mode
              </p>
            </div>
          </>
        ) : (
          <ReservationPass
            reference={reference}
            color={color}
            days={days}
            pickup={pickup}
            ret={ret}
            name={name}
            total={pricing.total}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}

function SummaryRow({ label, value, muted }) {
  return (
    <div className="flex items-baseline justify-between text-sm">
      <span className={muted ? "text-neutral-500 text-xs" : "text-neutral-700"}>{label}</span>
      <span className={`tabular-nums ${muted ? "text-neutral-500 text-xs" : "text-neutral-900 font-medium"}`}>
        {value}
      </span>
    </div>
  );
}

// —— Reservation Pass ————————————————————————————————————————
function ReservationPass({ reference, color, days, pickup, ret, name, total, onClose }) {
  return (
    <div data-testid="moke-reservation-pass">
      {/* Header strip */}
      <div className="px-7 pt-6 pb-4 flex items-start justify-between" style={{ background: color.hex, color: "#111" }}>
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] opacity-70">Reservation pass</p>
          <p className="font-display text-2xl mt-1 leading-none">Demo Complete.</p>
          <p className="text-xs opacity-70 mt-2">Mock-issued for showcase purposes</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-[0.3em] opacity-70">Ref</p>
          <p className="font-mono text-sm mt-1" data-testid="moke-pass-ref">#{reference}</p>
        </div>
      </div>

      <div className="px-7 py-6 grid grid-cols-[1fr_auto] gap-6 items-center">
        <div className="space-y-2 text-sm">
          <PassRow k="Guest"   v={name || "Anonymous rider"} />
          <PassRow k="Vehicle" v={<>Mini Moke · <span style={{ color: color.hex }} className="font-medium">{color.name}</span></>} />
          <PassRow k="Dates"   v={`${pickup} → ${ret}`} />
          <PassRow k="Days"    v={`${days}`} />
          <PassRow k="Total"   v={<span className="font-display text-lg">{fmt(total)}</span>} />
        </div>
        <FakeQRCode accent={color.hex} seed={reference} />
      </div>

      <div className="px-7 pb-6 pt-2 flex items-center justify-between gap-3 border-t border-black/10">
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-emerald-700 font-semibold" data-testid="moke-pass-status">
          <CheckCircle2 size={14} /> Demo complete
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-neutral-500 hover:text-black underline underline-offset-4"
          data-testid="moke-pass-close"
        >
          Close pass
        </button>
      </div>
    </div>
  );
}

function PassRow({ k, v }) {
  return (
    <div className="flex items-baseline justify-between gap-6">
      <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 shrink-0">{k}</span>
      <span className="text-right text-neutral-900">{v}</span>
    </div>
  );
}

// A deterministic pretty "QR-ish" grid rendered from the reference so it
// looks like a scannable pass without shipping a full QR library.
function FakeQRCode({ accent, seed }) {
  const size = 21;
  // Simple deterministic hash → cells
  const cells = useMemo(() => {
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
    const out = [];
    for (let y = 0; y < size; y++) {
      const row = [];
      for (let x = 0; x < size; x++) {
        h = (h * 1103515245 + 12345) >>> 0;
        row.push((h & 1) === 1);
      }
      out.push(row);
    }
    // Force the 3 finder patterns (top-left, top-right, bottom-left) so it reads as QR
    const mark = (cx, cy) => {
      for (let y = 0; y < 7; y++) for (let x = 0; x < 7; x++) {
        const border = x === 0 || y === 0 || x === 6 || y === 6;
        const inner  = x >= 2 && x <= 4 && y >= 2 && y <= 4;
        out[cy + y][cx + x] = border || inner;
      }
    };
    mark(0, 0); mark(size - 7, 0); mark(0, size - 7);
    return out;
  }, [seed]);

  return (
    <div className="relative w-32 h-32 rounded-lg overflow-hidden bg-white p-1 border border-black/10 shadow-sm" data-testid="moke-pass-qr">
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full">
        {cells.flatMap((row, y) =>
          row.map((on, x) =>
            on ? <rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} fill="#111" /> : null,
          ),
        )}
        <rect x={size / 2 - 2} y={size / 2 - 2} width="4" height="4" fill={accent} />
      </svg>
      <span className="absolute bottom-1 right-1 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-neutral-500 bg-white/80 rounded px-1">
        <QrCode size={9} /> Scan
      </span>
    </div>
  );
}
